<template>
  <div class="refund-records-container">
    <h1 class="page-title">退款记录</h1>
    <el-card class="content-card">
      <div class="card-header">
        <div class="search-area">
          <el-input
            v-model="searchForm.keyword"
            placeholder="搜索订单ID、用户姓名或手机号"
            style="width: 300px; margin-right: 10px;"
            clearable
            prefix-icon="Search"
          >
          </el-input>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            style="width: 300px; margin-right: 10px;"
            clearable
          />
          <el-button type="primary" @click="handleSearch" icon="Search">搜索</el-button>
        </div>
      </div>
      <div class="card-content">
        <el-table :data="refunds" border stripe style="width: 100%" v-loading="loading">
          <el-table-column label="退款信息" width="300">
            <template #default="scope">
              <div style="font-weight: bold; margin-bottom: 4px">退款单号: {{ scope.row.refund_no }}</div>
              <div style="font-size: 12px; margin-bottom: 4px">订单ID: {{ scope.row.order.id }}</div>
              <div style="font-size: 12px">订单号: {{ scope.row.order.order_no }}</div>
            </template>
          </el-table-column>
          <el-table-column label="用户信息" width="150">
            <template #default="scope">
              <div>{{ scope.row.order.user.name }}</div>
              <div style="font-size: 12px; color: #909399">{{ scope.row.order.user.phone }}</div>
            </template>
          </el-table-column>
          <el-table-column label="退款金额" width="120" align="right">
            <template #default="scope">
              <span>¥{{ scope.row.refund_amount }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="退款状态" width="120">
            <template #default="scope">
              <el-tag :type="getRefundStatusTagType(scope.row.status)">
                {{ getRefundStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="退款原因" width="200">
            <template #default="scope">
              <span>{{ scope.row.reason }}</span>
            </template>
          </el-table-column>
          <el-table-column label="创建时间" width="180">
            <template #default="scope">
              {{ new Date(scope.row.created_at).toISOString().slice(0, 19).replace('T', ' ') }}
            </template>
          </el-table-column>
          <el-table-column label="处理时间" width="180">
            <template #default="scope">
              {{ scope.row.processed_at ? new Date(scope.row.processed_at).toISOString().slice(0, 19).replace('T', ' ') : '未处理' }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button type="primary" size="small" @click="handleViewDetail(scope.row)">
                查看详情
              </el-button>
              <el-button 
                v-if="scope.row.status === 'failed'" 
                type="warning" 
                size="small" 
                @click="handleRetryRefund(scope.row)"
              >
                重新发起
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-container">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>
    
    <!-- 退款详情抽屉 -->
    <el-drawer
      v-model="detailDialogVisible"
      :title="`退款详情 - ${currentRefund?.refund_no || ''}`"
      direction="rtl"
      size="600px"
    >
      <div class="refund-detail" v-if="currentRefund">
        <el-card shadow="never" class="detail-card">
          <el-descriptions :column="1" border>
            <el-descriptions-item label="退款单号">{{ currentRefund.refund_no }}</el-descriptions-item>
            <el-descriptions-item label="订单ID">{{ currentRefund.order.id }}</el-descriptions-item>
            <el-descriptions-item label="订单号">{{ currentRefund.order.order_no }}</el-descriptions-item>
            <el-descriptions-item label="用户姓名">{{ currentRefund.order.user.name }}</el-descriptions-item>
            <el-descriptions-item label="用户手机号">{{ currentRefund.order.user.phone }}</el-descriptions-item>
            <el-descriptions-item label="订单金额">¥{{ currentRefund.order.total_amount }}</el-descriptions-item>
            <el-descriptions-item label="退款金额">¥{{ currentRefund.refund_amount }}</el-descriptions-item>
            <el-descriptions-item label="退款状态">
              <el-tag :type="getRefundStatusTagType(currentRefund.status)">
                {{ getRefundStatusText(currentRefund.status) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="退款原因">{{ currentRefund.reason }}</el-descriptions-item>
            <el-descriptions-item label="支付渠道">{{ currentRefund.pay_channel }}</el-descriptions-item>
            <el-descriptions-item label="交易单号">{{ currentRefund.trade_no }}</el-descriptions-item>
            <el-descriptions-item label="退款交易单号">{{ currentRefund.refund_trade_no || '未生成' }}</el-descriptions-item>
            <el-descriptions-item label="备注">{{ currentRefund.remark || '无' }}</el-descriptions-item>
            <el-descriptions-item label="创建时间">{{ currentRefund.created_at }}</el-descriptions-item>
            <el-descriptions-item label="处理时间">{{ currentRefund.processed_at || '未处理' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>
        
        <el-card shadow="never" class="detail-card mt-20">
          <template #header>
            <h3 class="detail-section-title m-0">商品信息</h3>
          </template>
          <el-table :data="currentRefund.order.items" border stripe style="width: 100%" size="small">
            <el-table-column prop="product.name" label="商品名称" />
            <el-table-column prop="quantity" label="数量" width="100" align="center" />
            <el-table-column prop="unit_price" label="单价" width="120" align="right">
              <template #default="scope">
                <span>¥{{ scope.row.unit_price }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="subtotal" label="小计" width="120" align="right">
              <template #default="scope">
                <span>¥{{ scope.row.subtotal }}</span>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, watch } from 'vue';
import { ElMessage } from 'element-plus';
import request from '../../api/axios';

// 退款记录数据
const refunds = ref([]);
// 搜索表单数据
const searchForm = reactive({
  keyword: '',
  start_date: '',
  end_date: ''
});
// 日期范围
const dateRange = ref([]);
// 分页数据
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
});
// loading状态
const loading = ref(false);
// 退款详情对话框
const detailDialogVisible = ref(false);
const currentRefund = ref(null);

// 监听日期范围变化
watch(
  () => dateRange.value,
  (newVal) => {
    if (newVal && newVal.length === 2) {
      searchForm.start_date = newVal[0];
      searchForm.end_date = newVal[1];
    } else {
      searchForm.start_date = '';
      searchForm.end_date = '';
    }
  }
);

// 获取退款记录
const fetchRefunds = async () => {
  loading.value = true;
  try {
    const response = await request({
      url: '/manage/refund-records',
      method: 'get',
      params: {
        keyword: searchForm.keyword,
        start_date: searchForm.start_date,
        end_date: searchForm.end_date,
        page: pagination.currentPage,
        pageSize: pagination.pageSize
      }
    });
    refunds.value = response.data.data || [];
    pagination.total = response.data.total || 0;
  } catch (error) {
    ElMessage.error('获取退款记录失败');
    console.error('获取退款记录失败:', error);
  } finally {
    loading.value = false;
  }
};

// 页面加载时获取数据
onMounted(() => {
  fetchRefunds();
});

// 搜索退款记录
// eslint-disable-next-line no-unused-vars
const handleSearch = () => {
  pagination.currentPage = 1;
  fetchRefunds();
};

// 分页大小变化
// eslint-disable-next-line no-unused-vars
const handleSizeChange = (size) => {
  pagination.pageSize = size;
  fetchRefunds();
};

// 当前页码变化
// eslint-disable-next-line no-unused-vars
const handleCurrentChange = (current) => {
  pagination.currentPage = current;
  fetchRefunds();
};

// 查看详情
// eslint-disable-next-line no-unused-vars
const handleViewDetail = (row) => {
  currentRefund.value = row;
  detailDialogVisible.value = true;
};

// 重新发起退款
// eslint-disable-next-line no-unused-vars
const handleRetryRefund = async (row) => {
  try {
    await request({
      url: `/manage/refund-records/${row.id}/retry`,
      method: 'post'
    });
    ElMessage.success('退款已重新发起');
    fetchRefunds();
  } catch (error) {
    ElMessage.error('重新发起退款失败：' + (error.response?.data?.message || error.message));
    console.error('重新发起退款失败:', error);
  }
};

// 获取退款状态标签类型
// eslint-disable-next-line no-unused-vars
const getRefundStatusTagType = (status) => {
  const statusMap = {
    'pending': 'warning',
    'processing': 'info',
    'success': 'success',
    'failed': 'danger'
  };
  return statusMap[status] || 'info';
};

// 获取退款状态文本
// eslint-disable-next-line no-unused-vars
const getRefundStatusText = (status) => {
  const statusMap = {
    'pending': '待处理',
    'processing': '处理中',
    'success': '成功',
    'failed': '失败'
  };
  return statusMap[status] || status;
};

// 获取订单状态标签类型
// eslint-disable-next-line no-unused-vars
const getStatusTagType = (status) => {
  const statusMap = {
    'pending': 'warning',
    'paid': 'primary',
    'shipped': 'info',
    'delivered': 'success',
    'cancelled': 'danger',
    'refunded': 'danger'
  };
  return statusMap[status] || 'info';
};

// 获取订单状态文本
// eslint-disable-next-line no-unused-vars
const getStatusText = (status) => {
  const statusMap = {
    'pending': '待支付',
    'paid': '已支付',
    'shipped': '已发货',
    'delivered': '已完成',
    'cancelled': '已取消',
    'refunded': '已退款'
  };
  return statusMap[status] || status;
};
</script>

<style scoped>
.refund-records-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.search-area {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.card-content {
  padding: 20px 0;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.refund-item-product {
  margin-bottom: 4px;
}

.detail-section-title {
  margin: 20px 0 10px;
  font-size: 16px;
  font-weight: bold;
}

/* 退款详情抽屉样式 */
.refund-detail {
  padding: 0;
}

.detail-card {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
}

.mt-20 {
  margin-top: 20px;
}

.el-descriptions {
  margin-bottom: 0;
}

.el-descriptions__label {
  font-weight: bold;
  color: #606266;
}

.el-descriptions__value {
  color: #303133;
}

.el-drawer__header {
  border-bottom: 1px solid #ebeef5;
  padding: 16px 20px;
  margin-bottom: 20px;
}

.el-drawer__title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.el-drawer__body {
  padding: 0 20px 20px;
}

.el-card__header {
  padding: 12px 20px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
}
</style>